/**
 * Subject palette for Wochenplaner.
 *
 * Each entry maps a subject ID to its Catppuccin-based color tokens and a
 * short display abbreviation. Tint values reference CSS custom properties
 * defined in swiss.css so that they automatically switch between the Latte
 * (light) and Mocha (dark) palettes when `.dark-mode` is toggled.
 *
 * Supported subject IDs: math, german, english, science, french, sport,
 * rzg, anlass, erg, sol, mi, wah.
 */
export const SUBJECT_STYLES = {
  math:      { tint: 'var(--subj-math-tint)',      ink: 'var(--subj-math-ink)',      short: 'MA' },
  german:    { tint: 'var(--subj-german-tint)',    ink: 'var(--subj-german-ink)',    short: 'DE' },
  english:   { tint: 'var(--subj-english-tint)',   ink: 'var(--subj-english-ink)',   short: 'EN' },
  science:   { tint: 'var(--subj-science-tint)',   ink: 'var(--subj-science-ink)',   short: 'NW' },
  rzg:       { tint: 'var(--subj-rzg-tint)',       ink: 'var(--subj-rzg-ink)',       short: 'RZG' },
  anlass:    { tint: 'var(--subj-anlass-tint)',    ink: 'var(--subj-anlass-ink)',    short: 'ANL' },
  erg:       { tint: 'var(--subj-erg-tint)',       ink: 'var(--subj-erg-ink)',       short: 'ERG' },
  sol:       { tint: 'var(--subj-sol-tint)',       ink: 'var(--subj-sol-ink)',       short: 'SOL' },
  mi:        { tint: 'var(--subj-mi-tint)',        ink: 'var(--subj-mi-ink)',        short: 'M&I' },
  wah:       { tint: 'var(--subj-wah-tint)',       ink: 'var(--subj-wah-ink)',       short: 'WAH' },
  french:    { tint: 'var(--subj-french-tint)',    ink: 'var(--subj-french-ink)',    short: 'FR' },
  sport:     { tint: 'var(--subj-sport-tint)',     ink: 'var(--subj-sport-ink)',     short: 'SP' },
};

/**
 * Returns the style object for a given subject ID, or a safe fallback for
 * unknown IDs.
 *
 * @param {string} id - A subject ID (e.g. `'math'`, `'german'`).
 * @param {Array<{id: string, color?: string, short?: string}>} [subjects] -
 *   The fetched subjects list (from `/api/admin/subjects`). When the matching
 *   entry has a custom `color` (teacher-picked, hex), it overrides the
 *   built-in Catppuccin token pair — the tint is derived from that color via
 *   `color-mix()` so it still adapts (roughly) between light/dark mode.
 * @returns {{ tint: string, ink: string, short: string }} Style tokens for
 *   the subject, falling back to neutral surface colours and a `'—'` short
 *   label when the ID is not recognised.
 */
export function getSubjectStyle(id, subjects) {
  const entry = subjects?.find(s => s.id === id);
  if (entry?.color) {
    return {
      tint: `color-mix(in srgb, ${entry.color} 18%, var(--surface))`,
      ink: entry.color,
      short: entry.short || SUBJECT_STYLES[id]?.short || '—',
    };
  }
  return SUBJECT_STYLES[id] || { tint: 'var(--surface)', ink: 'var(--ink-3)', short: '—' };
}
